import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { formatDistanceToNow } from 'date-fns';
import { useInstallationFieldUpdate, useInstallationBatchUpdate } from '@/hooks/use-installation-field-update';
import {
  InlineTextField,
  InlineSelectField,
  InlineDateField,
  InlineStatusSelect,
} from '@/components/ui/inline-edit';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  Cpu,
  ClipboardCheck,
  Info,
  Banknote,
  FileText,
  FileCheck,
  AlertCircle,
  Loader2,
  Check,
  Zap,
} from 'lucide-react';
import { BatterySerialNumbers } from './battery-serial-numbers';
import { DocumentViewer } from '@/features/operations/components/installations/document-viewer';
import { CompletionOfWorksRecords } from '@/features/operations/components/installations/completion-of-works-records';
import type { Installation, Customer } from '@shared/types/installation';

interface InstallationInlineFormProps {
  installationId: number;
}

export function InstallationInlineForm({ installationId }: InstallationInlineFormProps) {
  const [, navigate] = useLocation();
  const fieldUpdate = useInstallationFieldUpdate();
  const batchUpdate = useInstallationBatchUpdate();

  // Track saving state and last saved time for user feedback
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [, setForceUpdate] = useState(0);

  // Update the "saved X ago" text periodically
  useEffect(() => {
    if (!lastSavedTime) return;
    const interval = setInterval(() => {
      setForceUpdate(n => n + 1);
    }, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [lastSavedTime]);

  // Warn user before leaving with unsaved changes (during active save)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSaving]);

  // Fetch installation data - use default queryFn to match InstallationDetail behavior
  const { data: installation, isLoading, error } = useQuery<Installation>({
    queryKey: [`/api/installations/${installationId}`],
    enabled: !!installationId,
    staleTime: 0, // Always fetch fresh data on edit page
    retry: 2,
  });

  // Fetch customers for dropdown - use default queryFn
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['/api/customers'],
  });

  const handleFieldUpdate = async (field: string, value: any) => {
    setIsSaving(true);
    try {
      await fieldUpdate.mutateAsync({
        installationId,
        field,
        value,
      });
      setLastSavedTime(new Date());
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load installation data. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  // Status options aligned with Grid View and Shared Types
  const statusOptions = [
    { value: 'loa_mpan', label: 'LoA & MPAN' },
    { value: 'g98', label: 'G98' },
    { value: 'g99', label: 'Waiting DNO (G99)' },
    { value: 'site_survey', label: 'Site Survey Done' },
    { value: 'ready_to_schedule', label: 'Ready to Schedule' },
    { value: 'planning_for_installation', label: 'Planning' },
    { value: 'prepared_to_install', label: 'Prepared' },
    { value: 'completed', label: 'Completed' },
  ];

  const dnoProviderOptions = [
    { value: 'UK Power Networks', label: 'UK Power Networks' },
    { value: 'Northern Powergrid', label: 'Northern Powergrid' },
    { value: 'Scottish Power', label: 'Scottish Power' },
    { value: 'SSE Networks', label: 'SSE Networks' },
    { value: 'Western Power Distribution', label: 'Western Power Distribution' },
    { value: 'Electricity North West', label: 'Electricity North West' },
  ];

  const applicationTypeOptions = [
    { value: 'G98', label: 'G98 (Fast Track)' },
    { value: 'G99', label: 'G99 (Standard)' },
  ];

  const customerOptions = customers.map((c: any) => ({
    value: c.id.toString(),
    label: `${c.firstName} ${c.lastName}`,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/installations')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Installation #{installationId}</h1>
            <p className="text-muted-foreground">
              {installation?.customerName || installation?.customer?.name || 'No customer'} • {installation?.installationAddress || 'No address'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Save status indicator */}
          <div className="flex items-center gap-2 text-sm">
            {isSaving ? (
              <span className="flex items-center gap-1 text-amber-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </span>
            ) : lastSavedTime ? (
              <span className="flex items-center gap-1 text-green-600">
                <Check className="h-3 w-3" />
                Saved {formatDistanceToNow(lastSavedTime, { addSuffix: true })}
              </span>
            ) : null}
          </div>
          <Badge>{installation?.status || 'pending'}</Badge>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <div className="overflow-x-auto scrollbar-hide">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 min-w-[600px]">
            <TabsTrigger value="overview" className="flex items-center gap-1 text-xs sm:text-sm">
              <Info className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="checklist" className="flex items-center gap-1 text-xs sm:text-sm">
              <ClipboardCheck className="h-4 w-4" />
              <span>Checklist</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-1 text-xs sm:text-sm">
              <Banknote className="h-4 w-4" />
              <span>Payments</span>
            </TabsTrigger>
            <TabsTrigger value="technical" className="flex items-center gap-1 text-xs sm:text-sm">
              <Cpu className="h-4 w-4" />
              <span>Technical</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-1 text-xs sm:text-sm">
              <FileText className="h-4 w-4" />
              <span>Documents</span>
            </TabsTrigger>
            <TabsTrigger value="completion-records" className="flex items-center gap-1 text-xs sm:text-sm">
              <FileCheck className="h-4 w-4" />
              <span>Completion</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab - Combined Details & Scheduling */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Installation Overview
              </CardTitle>
              <CardDescription>
                Click on any field to edit it. Changes are saved automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer & Basic Info */}
              <div>
                <h3 className="text-sm font-medium mb-3">Customer & Basic Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Customer</label>
                    <InlineSelectField
                      value={installation?.customerId?.toString()}
                      options={customerOptions}
                      onSave={(value) => handleFieldUpdate('customerId', parseInt(value))}
                      placeholder="Select customer"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Status</label>
                    <InlineSelectField
                      value={installation?.status}
                      options={statusOptions}
                      onSave={(value) => handleFieldUpdate('status', value)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Lead Source</label>
                    <InlineTextField
                      value={installation?.leadSource}
                      onSave={(value) => handleFieldUpdate('leadSource', value)}
                      placeholder="Enter lead source"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Lead Owner</label>
                    <InlineTextField
                      value={installation?.leadOwner}
                      onSave={(value) => handleFieldUpdate('leadOwner', value)}
                      placeholder="Enter lead owner"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Installation Address</label>
                    <InlineTextField
                      value={installation?.installationAddress}
                      onSave={(value) => handleFieldUpdate('installationAddress', value)}
                      placeholder="Enter installation address"
                      multiline
                    />
                  </div>
                </div>
              </div>

              {/* Key Dates */}
              <div>
                <h3 className="text-sm font-medium mb-3">Key Dates</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Contract Signed</label>
                    <InlineDateField
                      value={installation?.contractSigned ? new Date(installation.contractSigned) : null}
                      onSave={(value) => handleFieldUpdate('contractSigned', value)}
                      placeholder="Select contract signed date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Scheduled Date</label>
                    <InlineDateField
                      value={installation?.scheduledDate ? new Date(installation.scheduledDate) : null}
                      onSave={(value) => handleFieldUpdate('scheduledDate', value)}
                      placeholder="Select scheduled date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Installation Date</label>
                    <InlineDateField
                      value={installation?.installationDate ? new Date(installation.installationDate) : null}
                      onSave={(value) => handleFieldUpdate('installationDate', value)}
                      placeholder="Select installation date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Commissioning Date</label>
                    <InlineDateField
                      value={installation?.commissioningDate ? new Date(installation.commissioningDate) : null}
                      onSave={(value) => handleFieldUpdate('commissioningDate', value)}
                      placeholder="Select commissioning date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Completed Date</label>
                    <InlineDateField
                      value={installation?.completedDate ? new Date(installation.completedDate) : null}
                      onSave={(value) => handleFieldUpdate('completedDate', value)}
                      placeholder="Select completed date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Installer Names</label>
                    <InlineTextField
                      value={installation?.installerNames}
                      onSave={(value) => handleFieldUpdate('installerNames', value)}
                      placeholder="Enter installer names"
                    />
                  </div>
                </div>
              </div>

              {/* Pre-Install Tracking */}
              <div>
                <h3 className="text-sm font-medium mb-3">Pre-Install Tracking</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Folder Created</label>
                    <InlineSelectField
                      value={installation?.folderCreated ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('folderCreated', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Tradify Added</label>
                    <InlineSelectField
                      value={installation?.tradifyAdded ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('tradifyAdded', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Job Board Added</label>
                    <InlineSelectField
                      value={installation?.jobBoardAdded ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('jobBoardAdded', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Job Board Removed</label>
                    <InlineSelectField
                      value={installation?.jobBoardRemoved ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('jobBoardRemoved', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* Scheduling Extended */}
              <div>
                <h3 className="text-sm font-medium mb-3">Scheduling Extended</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Site Visit Date</label>
                    <InlineDateField
                      value={installation?.siteVisitDate ? new Date(installation.siteVisitDate) : null}
                      onSave={(value) => handleFieldUpdate('siteVisitDate', value)}
                      placeholder="Select site visit date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">7-Day Invoice Issued</label>
                    <InlineSelectField
                      value={installation?.sevenDayInvoiceIssued ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('sevenDayInvoiceIssued', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">RAMS Completed</label>
                    <InlineSelectField
                      value={installation?.ramsCompleted ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('ramsCompleted', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* On-Site Tracking */}
              <div>
                <h3 className="text-sm font-medium mb-3">On-Site Tracking</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">EVCP Response</label>
                    <InlineTextField
                      value={installation?.evcpResponse}
                      onSave={(value) => handleFieldUpdate('evcpResponse', value)}
                      placeholder="Enter EVCP response"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">On-site Sign-off</label>
                    <InlineSelectField
                      value={installation?.onsiteSignoffCompleted ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('onsiteSignoffCompleted', value === 'Yes')}
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-1">
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">System Alterations</label>
                    <InlineTextField
                      value={installation?.systemAlterations}
                      onSave={(value) => handleFieldUpdate('systemAlterations', value)}
                      placeholder="Enter alterations"
                      multiline
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Balance Invoice Issued</label>
                    <InlineSelectField
                      value={installation?.balanceInvoiceIssued ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('balanceInvoiceIssued', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Next Steps Email Sent</label>
                    <InlineSelectField
                      value={installation?.nextStepsEmailSent ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('nextStepsEmailSent', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">EVCP Commissioning Sent</label>
                    <InlineSelectField
                      value={installation?.evcpCommissioningSent ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('evcpCommissioningSent', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Maintenance Schedule Provided</label>
                    <InlineSelectField
                      value={installation?.maintenanceScheduleProvided ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('maintenanceScheduleProvided', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Installation Notes
                </h3>
                <InlineTextField
                  value={installation?.notes}
                  onSave={(value) => handleFieldUpdate('notes', value)}
                  placeholder="Add notes for this installation..."
                  multiline
                  className="w-full min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist">
          <Card>
            <CardHeader>
              <CardTitle>Installation Checklist</CardTitle>
              <CardDescription>
                Track completion status of installation tasks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">HIES Registered</span>
                  <InlineStatusSelect
                    value={installation?.checklistHiesRegistered || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistHiesRegistered', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">HIES Return Completed</span>
                  <InlineStatusSelect
                    value={installation?.checklistHiesReturnCompleted || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistHiesReturnCompleted', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">DNO Commissioning Sent</span>
                  <InlineStatusSelect
                    value={installation?.checklistDnoCommissioningSent || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistDnoCommissioningSent', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">MCS Certificate Provided</span>
                  <InlineStatusSelect
                    value={installation?.checklistMcsCertificateProvided || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistMcsCertificateProvided', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">EIC Certificate Provided</span>
                  <InlineStatusSelect
                    value={installation?.checklistEicCertificateProvided || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistEicCertificateProvided', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">Part P Building Regs Certificate</span>
                  <InlineStatusSelect
                    value={installation?.checklistPartPBuildingRegsCertificate || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistPartPBuildingRegsCertificate', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">Final Payment Received</span>
                  <InlineStatusSelect
                    value={installation?.checklistFinalPaymentReceived || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistFinalPaymentReceived', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">PO for Scaffold</span>
                  <InlineStatusSelect
                    value={installation?.checklistPoForScaffold || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistPoForScaffold', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">Payment from Scaffold</span>
                  <InlineStatusSelect
                    value={installation?.checklistPaymentFromScaffold || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistPaymentFromScaffold', value)}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="font-medium">Customer Satisfaction Survey</span>
                  <InlineStatusSelect
                    value={installation?.checklistCustomerSatisfactionSurvey || 'pending'}
                    onSave={(value) => handleFieldUpdate('checklistCustomerSatisfactionSurvey', value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Payment Information
              </CardTitle>
              <CardDescription>
                Track payment details and invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Total Cost (£)</label>
                  <InlineTextField
                    value={installation?.totalCost}
                    onSave={(value) => handleFieldUpdate('totalCost', value)}
                    placeholder="Enter total cost"
                    type="number"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Deposit Amount (£)</label>
                  <InlineTextField
                    value={installation?.depositAmount}
                    onSave={(value) => handleFieldUpdate('depositAmount', value)}
                    placeholder="Enter deposit amount"
                    type="number"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Deposit Paid Date</label>
                  <InlineDateField
                    value={installation?.depositPaid ? new Date(installation.depositPaid) : null}
                    onSave={(value) => handleFieldUpdate('depositPaid', value)}
                    placeholder="Select deposit paid date"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">7-Day Payment Amount (£)</label>
                  <InlineTextField
                    value={installation?.sevenDayPaymentAmount}
                    onSave={(value) => handleFieldUpdate('sevenDayPaymentAmount', value)}
                    placeholder="Enter 7-day payment"
                    type="number"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">7-Day Payment Date</label>
                  <InlineDateField
                    value={installation?.sevenDayPaymentDate ? new Date(installation.sevenDayPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('sevenDayPaymentDate', value)}
                    placeholder="Select 7-day payment date"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Final Payment Amount (£)</label>
                  <InlineTextField
                    value={installation?.finalPaymentAmount}
                    onSave={(value) => handleFieldUpdate('finalPaymentAmount', value)}
                    placeholder="Enter final payment"
                    type="number"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Final Payment Date</label>
                  <InlineDateField
                    value={installation?.finalPaymentDate ? new Date(installation.finalPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('finalPaymentDate', value)}
                    placeholder="Select final payment date"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Additional Payment (£)</label>
                  <InlineTextField
                    value={installation?.additionalPaymentAmount}
                    onSave={(value) => handleFieldUpdate('additionalPaymentAmount', value)}
                    placeholder="Enter additional payment"
                    type="number"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Additional Payment Date</label>
                  <InlineDateField
                    value={installation?.additionalPaymentDate ? new Date(installation.additionalPaymentDate) : null}
                    onSave={(value) => handleFieldUpdate('additionalPaymentDate', value)}
                    placeholder="Select additional payment date"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Finance Provider</label>
                  <InlineTextField
                    value={installation?.finance}
                    onSave={(value) => handleFieldUpdate('finance', value)}
                    placeholder="Enter finance provider"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-1 block">Commission (£)</label>
                  <InlineTextField
                    value={installation?.commission}
                    onSave={(value) => handleFieldUpdate('commission', value)}
                    placeholder="Enter commission"
                    type="number"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab - Combined with DNO */}
        <TabsContent value="technical">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Technical Information
              </CardTitle>
              <CardDescription>
                DNO applications and system details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* DNO Application */}
              <div>
                <h3 className="text-sm font-medium mb-3">DNO Application</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">DNO Provider</label>
                    <InlineSelectField
                      value={installation?.dnoProvider}
                      options={dnoProviderOptions}
                      onSave={(value) => handleFieldUpdate('dnoProvider', value)}
                      placeholder="Select DNO provider"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Application Type</label>
                    <InlineSelectField
                      value={installation?.dnoApplicationType}
                      options={applicationTypeOptions}
                      onSave={(value) => handleFieldUpdate('dnoApplicationType', value)}
                      placeholder="Select application type"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Application Sent</label>
                    <InlineDateField
                      value={installation?.dnoApplicationSent ? new Date(installation.dnoApplicationSent) : null}
                      onSave={(value) => handleFieldUpdate('dnoApplicationSent', value)}
                      placeholder="Select date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">ENA Reference</label>
                    <InlineTextField
                      value={installation?.dnoSystemReference}
                      onSave={(value) => handleFieldUpdate('dnoSystemReference', value)}
                      placeholder="Enter ENA reference"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Case Reference</label>
                    <InlineTextField
                      value={installation?.dnoCaseReference}
                      onSave={(value) => handleFieldUpdate('dnoCaseReference', value)}
                      placeholder="Enter case reference"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">DNO Approval Date</label>
                    <InlineDateField
                      value={installation?.dnoApproved ? new Date(installation.dnoApproved) : null}
                      onSave={(value) => handleFieldUpdate('dnoApproved', value)}
                      placeholder="Select approval date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Export Limitation</label>
                    <InlineTextField
                      value={installation?.dnoExportLimitation}
                      onSave={(value) => handleFieldUpdate('dnoExportLimitation', value)}
                      placeholder="Enter export limitation"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">DNO Notes</label>
                    <InlineTextField
                      value={installation?.dnoNotes}
                      onSave={(value) => handleFieldUpdate('dnoNotes', value)}
                      placeholder="Add DNO notes..."
                      multiline
                    />
                  </div>
                </div>
              </div>

              {/* DNO Extended */}
              <div>
                <h3 className="text-sm font-medium mb-3">DNO Extended</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Offer Letter Received</label>
                    <InlineDateField
                      value={installation?.dnoOfferLetterReceived ? new Date(installation.dnoOfferLetterReceived) : null}
                      onSave={(value) => handleFieldUpdate('dnoOfferLetterReceived', value)}
                      placeholder="Select date"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Docusign Status</label>
                    <InlineSelectField
                      value={installation?.dnoDocusignStatus || ''}
                      options={[
                        { value: '', label: 'Not Started' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'sent', label: 'Sent' },
                        { value: 'signed', label: 'Signed' },
                      ]}
                      onSave={(value) => handleFieldUpdate('dnoDocusignStatus', value)}
                      placeholder="Select status"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">DNO Fee Paid</label>
                    <InlineSelectField
                      value={installation?.dnoFeePaid ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('dnoFeePaid', value === 'Yes')}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Limit Added to Survey</label>
                    <InlineSelectField
                      value={installation?.dnoLimitAddedToSurvey ? 'Yes' : 'No'}
                      options={[
                        { value: 'Yes', label: 'Yes' },
                        { value: 'No', label: 'No' },
                      ]}
                      onSave={(value) => handleFieldUpdate('dnoLimitAddedToSurvey', value === 'Yes')}
                    />
                  </div>
                </div>
              </div>

              {/* System Technical Details */}
              <div>
                <h3 className="text-sm font-medium mb-3">System Technical Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">System Size (kW)</label>
                    <InlineTextField
                      value={installation?.systemSize}
                      onSave={(value) => handleFieldUpdate('systemSize', value)}
                      placeholder="Enter system size"
                      type="number"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Panel Quantity</label>
                    <InlineTextField
                      value={installation?.panelCount}
                      onSave={(value) => handleFieldUpdate('panelCount', value)}
                      placeholder="Enter panel quantity"
                      type="number"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Connection Type</label>
                    <InlineSelectField
                      value={installation?.connectionType}
                      options={[
                        { value: 'Single Phase', label: 'Single Phase' },
                        { value: 'Three Phase', label: 'Three Phase' },
                      ]}
                      onSave={(value) => handleFieldUpdate('connectionType', value)}
                      placeholder="Select connection type"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">MPAN Number</label>
                    <InlineTextField
                      value={installation?.mpanNumber}
                      onSave={(value) => handleFieldUpdate('mpanNumber', value)}
                      placeholder="Enter MPAN"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Export MPAN</label>
                    <InlineTextField
                      value={installation?.exportMpan}
                      onSave={(value) => handleFieldUpdate('exportMpan', value)}
                      placeholder="Enter export MPAN"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Inverter Serial Number</label>
                    <InlineTextField
                      value={installation?.inverterSerialNumber}
                      onSave={(value) => handleFieldUpdate('inverterSerialNumber', value)}
                      placeholder="Enter serial number"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Battery Serial Number</label>
                    <InlineTextField
                      value={installation?.batterySerialNumber}
                      onSave={(value) => handleFieldUpdate('batterySerialNumber', value)}
                      placeholder="Enter serial number"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Panel Model</label>
                    <InlineTextField
                      value={installation?.panelModel}
                      onSave={(value) => handleFieldUpdate('panelModel', value)}
                      placeholder="Enter panel model"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Battery Model</label>
                    <InlineTextField
                      value={installation?.batteryModel}
                      onSave={(value) => handleFieldUpdate('batteryModel', value)}
                      placeholder="Enter battery model"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">Battery Qty</label>
                    <InlineTextField
                      value={installation?.batteryQty}
                      onSave={(value) => handleFieldUpdate('batteryQty', value)}
                      placeholder="Enter quantity"
                      type="number"
                    />
                  </div>
                </div>
              </div>

              {/* Battery Serial Numbers Component */}
              {installationId && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium mb-3">Additional Battery Details</h3>
                  <BatterySerialNumbers installationId={installationId} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents & Certificates
              </CardTitle>
              <CardDescription>
                View installation documents and certificates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Finance Documents */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-primary" />
                  Finance Documents
                </h3>
                <DocumentViewer
                  documents={(installation?.financeDocuments || []) as any[]}
                  title=""
                  emptyMessage="No finance documents have been uploaded"
                />
              </div>

              {/* DNO Documents */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  DNO Application Documents
                </h3>
                <DocumentViewer
                  documents={(installation?.dnoDocuments || []) as any[]}
                  title=""
                  emptyMessage="No DNO documents have been uploaded"
                />
              </div>

              {/* Technical Documents */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-primary" />
                  Technical Documents
                </h3>
                <DocumentViewer
                  documents={(installation?.technicalDocuments || []) as any[]}
                  title=""
                  emptyMessage="No technical documents have been uploaded"
                />
              </div>

              {/* Handover Documents */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileCheck className="h-4 w-4 text-primary" />
                  Handover Documents
                </h3>
                <DocumentViewer
                  documents={(installation?.handoverDocuments || []) as any[]}
                  title=""
                  emptyMessage="No handover documents have been uploaded"
                />
              </div>

              {/* Contract Documents */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Contract Documents
                </h3>
                <DocumentViewer
                  documents={(installation?.contractDocuments || []) as any[]}
                  title=""
                  emptyMessage="No contract documents have been uploaded"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completion Records Tab */}
        <TabsContent value="completion-records">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Completion of Works Records
              </CardTitle>
              <CardDescription>
                Manage completion of works records for customer sign-off
              </CardDescription>
            </CardHeader>
            <CardContent>
              {installation ? (
                <CompletionOfWorksRecords
                  installationId={installationId}
                  installation={installation}
                  customer={customers.find((c: Customer) => c.id === installation?.customerId)}
                />
              ) : (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">
                    Unable to load completion of works records. Please try refreshing the page.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}